import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Paper,
} from '@mui/material';
import { useCV } from '../contexts/CVContext';
import '@fontsource/lato/300.css';
import '@fontsource/lato/400.css';
import '@fontsource/lato/700.css';
import {
  DOCUMENT_TYPES,
  prepareTemplateData,
  createDocxTemplate,
} from '../helpers/ProcessTemplate';
import { renderAsync } from "docx-preview";
import debounce from 'lodash.debounce';
import LoadingSpinner from '../../../common/LoadingSpinner';
import EmptyState from '../../../common/EmptyState';

const CVPreview = () => {
  const { cvData, cvDataWithMetadata } = useCV();

  const [zoomLevel, setZoomLevel] = useState(60);
  const [previewError, setPreviewError] = useState(null);
  const [generatedDocx, setGeneratedDocx] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [prevCvData, setPrevCvData] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const docxContainerRef = useRef(null);

  // Function to generate DOCX preview with loading state
  const generateDocxPreview = async (templateData) => {
    try {
      setIsLoading(true);
      setPreviewError(null);
      
      // Create and process template
      let doc;
      try {
        doc = await createDocxTemplate(templateData);
      } catch (templateError) {
        setPreviewError(templateError.message);
        setIsLoading(false);
        setIsInitialLoad(false);
        return;
      }

      // Get the result
      const blob = doc.getZip().generate({ type: 'blob' });
      setGeneratedDocx(blob);

      // Render preview if container is available
      if (docxContainerRef.current) {
        // Apply a fade-out effect before clearing
        const container = docxContainerRef.current;
        
        // Set up fade-out transition
        container.style.transition = 'opacity 0.2s ease';
        container.style.opacity = '0.3';
        
        // Wait for transition
        setTimeout(async () => {
          container.innerHTML = ''; // Clear previous content
          
          // Get the available width
          const containerWidth = container.offsetWidth || 816;
          
          try {
            await renderAsync(blob, container, null, {
              className: 'docx-viewer',
              inWrapper: false,
              hideWrapperOnPrint: true,
              ignoreWidth: false,
              ignoreHeight: false,
              breakPages: true,
              useBase64URL: true,
              overrideWidth: containerWidth, // Use container width
              renderHeaderFooter: false, // Disable headers/footers for better fitting
              enableAutoWidth: true, // Enable auto-width for better fitting
              trimXmlDeclaration: true,
              ignoreFonts: false,
              ignoreLastRenderedPageBreak: true,
              enableExperimentalFeatures: false,
              defaultFont: {
                fallbackFont: 'Arial',
                color: '#000000'
              },
              // Add a style override for text color
              fontStyle: 'color: black !important;',
            });
            
            // Fix extra space issue after rendering
            const fixExtraSpace = () => {
              // Find the last page in the document
              const pages = container.querySelectorAll('.docx-wrapper-page');
              if (pages.length > 0) {
                const lastPage = pages[pages.length - 1];
                const emptySpaceElements = lastPage.querySelectorAll('div:empty');
                
                // Remove any empty divs at the end of document
                emptySpaceElements.forEach(el => {
                  if (el.parentNode === lastPage && !el.textContent.trim()) {
                    el.style.display = 'none';
                  }
                });
                
                // Add a style to ensure last page doesn't have extra margin and text is black
                const style = document.createElement('style');
                style.textContent = `
                  .docx-wrapper-page:last-child { margin-bottom: 0 !important; }
                  .docx-wrapper-page:last-child > div:last-child:empty { display: none !important; }
                  .docx-viewer, .docx-viewer * { color: black !important; }
                  .docx-viewer p, .docx-viewer span, .docx-viewer div, .docx-viewer h1, 
                  .docx-viewer h2, .docx-viewer h3, .docx-viewer h4, .docx-viewer h5, 
                  .docx-viewer h6, .docx-viewer td, .docx-viewer th { color: black !important; }
                `;
                container.appendChild(style);
                
                // Also directly set text color for all text elements
                const textElements = container.querySelectorAll('p, span, div, h1, h2, h3, h4, h5, h6, td, th');
                textElements.forEach(el => {
                  el.style.color = 'black';
                });
              }
            };
            
            // Apply fix after rendering completes
            setTimeout(fixExtraSpace, 100);
            
            // Fade back in
            container.style.opacity = '1';
            
          } catch (renderError) {
            console.error('Render error:', renderError);
            setPreviewError(`Render error: ${renderError.message}`);
            container.style.opacity = '1';
            setIsLoading(false);
            setIsInitialLoad(false);
          }
          
          setIsLoading(false);
          setIsInitialLoad(false);
        }, 200);
      }
    } catch (error) {
      console.error('Error generating document:', error);
      setPreviewError(error.message || 'Error generating document preview');
      setIsLoading(false);
      setIsInitialLoad(false);
    }
  };

  // Debounced update function
  const debouncedUpdatePreview = useCallback(
    debounce((data) => {
      if (!docxContainerRef.current) {
        return;
      }
      
      try {
        // Generate template data from CV data
        const templateData = prepareTemplateData(data, DOCUMENT_TYPES.PREVIEW);
        
        // Only update if data has actually changed
        const currentDataStr = JSON.stringify(templateData);
        const prevDataStr = JSON.stringify(prevCvData ? prepareTemplateData(prevCvData, DOCUMENT_TYPES.PREVIEW) : {});
        
        if (currentDataStr !== prevDataStr) {
          // Store current data as previous for next comparison
          setPrevCvData(data);
          
          // Generate the preview
          generateDocxPreview(templateData);
        } else {
          // If data hasn't changed, still turn off loading state
          setIsLoading(false);
          setIsInitialLoad(false);
        }
      } catch (error) {
        console.error('Error in CV Preview:', error);
        setIsLoading(false);
        setIsInitialLoad(false);
      }
    }, 500),
    []
  );

  // Effect to update preview when CV data changes
  useEffect(() => {
    if (cvDataWithMetadata && docxContainerRef.current) {
      // Set loading state to true immediately when starting to process data
      setIsLoading(true);
      debouncedUpdatePreview(cvDataWithMetadata);
    } else if (!cvDataWithMetadata) {
      // If no CV data, stop loading
      setIsLoading(false);
      setIsInitialLoad(false);
    }
    
    // Cleanup
    return () => {
      debouncedUpdatePreview.cancel();
    };
  }, [cvDataWithMetadata, docxContainerRef.current]);

  // Set initial loading state
  useEffect(() => {
    // Set loading to true on initial mount
    setIsLoading(true);
    setIsInitialLoad(true);
    
    return () => {
    };
  }, []);

  // Expose the current document blob to parent components
  useEffect(() => {
    // You could add a callback here to notify parent about document updates
  }, [generatedDocx]);

  // If no data, show empty state
  if (!cvData || !Object.keys(cvData).some(key => cvData[key] && 
      (typeof cvData[key] === 'string' ? cvData[key].length > 0 : 
       Array.isArray(cvData[key]) ? cvData[key].length > 0 : true))) {
    return (
      <Paper sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No data available for preview
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ position: 'sticky', top: 20 }}>
      <Paper 
        elevation={0}
        sx={{ 
          p: 0, 
          overflow: 'auto',
          maxHeight: 'calc(100vh - 240px)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
          border: 'none',
          boxShadow: 'none',
          minHeight: 'calc(100vh - 240px)',
          backgroundColor: isInitialLoad ? '#f8f9fa' : 'transparent'
        }}
      >
        {/* Loading Spinner */}
        {isLoading && (
          <Box 
            sx={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              backgroundColor: isInitialLoad ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.7)', 
              zIndex: 10
            }}
          >
            <LoadingSpinner message={isInitialLoad ? "Loading CV preview..." : "Updating CV preview..."} />
          </Box>
        )}
        
        {/* Content */}
        {previewError ? (
          <EmptyState 
            title="Error rendering CV preview" 
            description={previewError}
            textSize="h8"
          />
        ) : (
          <Box 
            sx={{
              width: '100%',
              maxWidth: '816px', // A4 width at 96 DPI
              transform: `scale(${zoomLevel / 100})`,
              transformOrigin: 'top left',
              transition: 'transform 0.2s ease',
              height: 'auto',
              minHeight: zoomLevel < 100 ? '100%' : 'auto'
            }}
          >
            <Box 
              ref={docxContainerRef}
              sx={{ 
                '& .docx-viewer': {
                  backgroundColor: '#f8f9fa !important',
                  color: 'black !important',
                },
                '& .docx-viewer *': {
                  color: 'black !important',
                },
                '& .docx-viewer p, & .docx-viewer span, & .docx-viewer div, & .docx-viewer h1, & .docx-viewer h2, & .docx-viewer h3, & .docx-viewer h4, & .docx-viewer h5, & .docx-viewer h6, & .docx-viewer td, & .docx-viewer th': {
                  color: 'black !important',
                },
                '& .docx-viewer table ': {
                  border: '1px solid transparent',
                  borderCollapse: 'collapse',
                  outline: 'none',
                },
                '& .docx-viewer table td': {
                  borderTop: '1px solid transparent !important',
                  borderBottom: '1px solid transparent !important',
                  color: 'black !important',
                },
                '& .docx-viewer table td p::before': {
                  content: 'none',
                },
              }}
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default CVPreview;